import { useEffect, useRef, useState } from 'react';

import {
  CfPropagationCheckResponse,
  useCheckCfPropagationMutation,
} from '../api/cloudflarePropagationCheckApi';
import { CfFlowStatus } from '../api/cloudflareFlowApi';
import { httpStatusOf } from '../api/httpStatusOf';

const TOO_MANY_REQUESTS = 429;

export type CfPropagationRecheckProblem = 'cloudflare_unreachable' | 'throttled' | 'request_failed' | 'beta_closed';

export type CfPropagationRecheckOptions = {
  inBackground?: boolean;
};

export type CfPropagationRecheckOutcome = {
  /** The check found no problem to show. */
  ok: boolean;
  /** The zone was active, so the server moved the flow off the wait. */
  movedOn: boolean;
};

const NOT_CHECKED: CfPropagationRecheckOutcome = { ok: false, movedOn: false };

const WAITING_STATUSES: readonly CfFlowStatus[] = ['awaiting_nameservers', 'awaiting_propagation', 'propagation_timed_out'];

export type CfPropagationRecheck = {
  check: (options?: CfPropagationRecheckOptions) => Promise<CfPropagationRecheckOutcome>;
  isChecking: boolean;
  problem: CfPropagationRecheckProblem | null;
};

const problemIn = (answer: CfPropagationCheckResponse): CfPropagationRecheckProblem | null => {
  if (answer.refusal === 'beta_closed') return 'beta_closed';

  return answer.unavailable ? 'cloudflare_unreachable' : null;
};

export const useCfPropagationRecheck = (domain: string, onScreen: boolean): CfPropagationRecheck => {
  const [checkCfPropagation] = useCheckCfPropagationMutation();
  const [isChecking, setIsChecking] = useState(false);
  const [problem, setProblem] = useState<CfPropagationRecheckProblem | null>(null);
  const stillOnScreen = useRef(onScreen);
  const inFlight = useRef(false);

  useEffect(() => {
    stillOnScreen.current = onScreen;
    if (onScreen) return;

    setProblem(null);
  }, [onScreen]);

  const showProblem = (found: CfPropagationRecheckProblem | null) => {
    if (stillOnScreen.current) setProblem(found);
  };

  const check = async ({
    inBackground = false,
  }: CfPropagationRecheckOptions = {}): Promise<CfPropagationRecheckOutcome> => {
    if (inFlight.current) return NOT_CHECKED;

    inFlight.current = true;
    setIsChecking(true);
    if (!inBackground) setProblem(null);
    try {
      const answer = await checkCfPropagation({ domain }).unwrap();
      const found = problemIn(answer);
      const movedOn = answer.started || !WAITING_STATUSES.includes(answer.status);
      if (inBackground && found === 'cloudflare_unreachable') return { ok: false, movedOn };

      showProblem(found);

      return { ok: found === null, movedOn };
    } catch (error) {
      if (!inBackground) showProblem(httpStatusOf(error) === TOO_MANY_REQUESTS ? 'throttled' : 'request_failed');

      return NOT_CHECKED;
    } finally {
      inFlight.current = false;
      setIsChecking(false);
    }
  };

  return { check, isChecking, problem };
};
